import React, { createContext, useCallback, useContext, useEffect } from "react";
import {
  Navigate,
  NavigateFunction,
  useLocation,
  useMatch,
  useNavigate,
  useOutlet,
  useParams,
} from "react-router-dom";
import ProtectedAccessRoute from "../../Navigation/ProtectedAccessRoute";
import styles from "./FullWidthBlade.module.css";

const BladeCloseContext = createContext<(() => void) | null>(null);

export function useListBlade() {
  const outlet = useOutlet();
  return { outlet, bladeOpen: Boolean(outlet) };
}

export function usePathBlade(pattern: string) {
  return Boolean(useMatch(pattern));
}

export function ProtectedListLayout({
  accessCode,
  list,
}: {
  accessCode: string;
  list: React.ReactNode;
}) {
  const { outlet } = useListBlade();
  return (
    <ProtectedAccessRoute
      authenticationPath="/login"
      accessCode={accessCode}
      requiredPermission="read"
      outlet={
        <>
          {list}
          {outlet}
        </>
      }
    />
  );
}

export function useInBlade() {
  return useContext(BladeCloseContext) != null;
}

export function useBladeBack(fallbackPath?: string) {
  const closeBlade = useContext(BladeCloseContext);
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(() => {
    if (closeBlade) {
      closeBlade();
      return;
    }
    if (location.key !== "default") {
      navigate(-1);
      return;
    }
    if (fallbackPath) navigate(fallbackPath);
  }, [closeBlade, fallbackPath, location.key, navigate]);
}

export function navigateToBlade(
  navigate: NavigateFunction,
  pathname: string,
  searchParams: URLSearchParams,
  extraState?: Record<string, unknown>
) {
  navigate(
    { pathname, search: searchParams.toString() },
    { state: { ...(extraState || {}), blade: true } }
  );
}

export function RedirectToNestedBlade({ toPrefix }: { toPrefix: string }) {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  return <Navigate to={`${toPrefix}/${id}${location.search}`} replace />;
}

type FullWidthBladeProps = {
  listPath: string;
  children: React.ReactNode;
};

const FullWidthBlade: React.FC<FullWidthBladeProps> = ({ listPath, children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const close = useCallback(() => {
    const search = location.search;
    if ((location.state as { blade?: boolean } | null)?.blade) {
      navigate(-1);
      return;
    }
    navigate({ pathname: listPath, search }, { replace: true });
  }, [listPath, location.search, location.state, navigate]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  return (
    <BladeCloseContext.Provider value={close}>
      <div className={styles.blade} role="dialog" aria-modal="true">
        {children}
      </div>
    </BladeCloseContext.Provider>
  );
};

export default FullWidthBlade;
